import { Component, OnDestroy } from '@angular/core';
import { ConfirmationService, MessageService } from 'primeng/api';
import { Subscription, catchError, tap } from 'rxjs';

import { FiltroTabelasAdministrativas } from '../models/filtro';
import { IncentivoApiService } from '../services/incentivo-api.service';
import { TabelasAdministrativas } from '@app/core/models/tabelasAdministrativas';
import { UiService } from '@app/core/services/ui.service';

@Component({
	templateUrl: './tabela-incentivo.component.html',
	providers: [MessageService, ConfirmationService]
})
export class TabelaIncentivoComponent implements OnDestroy {
	pesquisarAoCarregar = true;
	habilitarFiltroCadastradoUsuario = false;
	filtros?: FiltroTabelasAdministrativas;
	limparFormularioPesquisa = false;
	resultado: any[] = [];
	loadingResultado = false;
	totalResultado?: number;
	pagina = 1;
	itensPorPagina = 10;
	primeiroItemResultado = 0;
	colunas: any;
	itemTabela = new TabelasAdministrativas();
	operacao?: string;
	exibirModalManterTabela = false;
	tituloModalManterTabela = '';
	subscriptions: Subscription[] = [];
	registrosSelecionados: TabelasAdministrativas[] = [];

	constructor(
		private confirmationService: ConfirmationService,
		private tabelaService: IncentivoApiService,
		private messageService: MessageService,
		private ui: UiService
	) {}

	ngOnDestroy(): void {
		this.subscriptions.forEach(subscription => subscription.unsubscribe());
	}

	prepararCadastrar() {
		this.tituloModalManterTabela = 'Cadastrar incentivo';
		this.itemTabela = new TabelasAdministrativas();
		this.operacao = 'cadastrar';
		this.exibirModalManterTabela = true;
	}

	prepararAlterar(event: any) {
		this.subscriptions.push(
			this.tabelaService.obterIncentivo(event.id).subscribe(res => {
				this.itemTabela = new TabelasAdministrativas(res.id, res.nome, res.ativo);
				this.tituloModalManterTabela = 'Alterar incentivo';
				this.operacao = 'alterar';
				this.exibirModalManterTabela = true;
			})
		);
	}

	ativarInativarEmLote(ativar = true) {
		this.messageService.clear();
		if (this.registrosSelecionados.length === 0)
			return;

		const listaIdRegistrosSelecionados: number[] = [];
		for (const item of this.registrosSelecionados) {
			listaIdRegistrosSelecionados.push(item.id!);
		}
		const operacao = ativar ? 'ativado(s)' : 'inativado(s)';
		this.subscriptions.push(
			this.tabelaService
				.ativarInativarIncentivo(listaIdRegistrosSelecionados!, ativar)
				.pipe(
					tap(() => {
						this.criarMensagem('success', `Incentivo(s) ${operacao} com sucesso`);
						this.pesquisar();
					}),
					catchError(err => {
						this.criarMensagem('error', err.error.message);
						return [];
					})
				)
				.subscribe()
		);
	}

	salvarItem(event: TabelasAdministrativas) {
		const item = new TabelasAdministrativas(event.id, event.nome, event.ativo);
		item.id = event.id;
		item.nome = event.nome;
		item.ativo = event.ativo ? true : false;
		if (this.operacao === 'cadastrar') {
			this.salvarNovo(item);
		} else if (item.id) {
			this.alterar(item.id, item);
		}
		this.operacao = '';
		this.exibirModalManterTabela = false;
	}

	executarPesquisar(event: any) {
		this.filtros = new FiltroTabelasAdministrativas(undefined, event.nome, event.ativo);
		this.primeiroItemResultado = 0;
		this.pagina = 1;
		this.messageService.clear();
		this.pesquisar();
		this.criarColunas();
	}

	limparFormulario() {
		if (this.pesquisarAoCarregar) {
			this.executarPesquisar(new FiltroTabelasAdministrativas());
			this.limparFormularioPesquisa = true;
		} else {
			this.resultado = [];
		}
	}

	excluirItem(event: any) {
		const incentivo = event;
		this.confirmationService.confirm({
			message: 'Esta ação não poderá ser desfeita.',
			header: 'Deseja excluir o incentivo ' + incentivo.nome + '?',
			accept: async () => {
				this.tabelaService.excluirIncentivo(event.id).subscribe(
					async (res: any) => {
						this.exibirMensagemSucesso(res);
					},
					err => {
						this.criarMensagem('error', err.error.message);
					}
				);
			}
		});
	}

	fecharModal() {
		this.exibirModalManterTabela = false;
		this.itemTabela = new TabelasAdministrativas();
		this.operacao = 'cadastrar';
	}

	paginar(event: any) {
		this.pagina = event.first / event.rows + 1;
		this.primeiroItemResultado = event.first;
		this.itensPorPagina = event.rows;
		this.pesquisar();
	}

	getItensPorPagina(event: any) {
		this.itensPorPagina = event.rows;
	}

	private pesquisar() {
		this.resultado = [];
		this.loadingResultado = true;
		if (this.filtros) {
			this.subscriptions.push(
				this.tabelaService.obterIncentivos(this.filtros, this.pagina, this.itensPorPagina).subscribe(
					(resultado: any) => {
						this.loadingResultado = false;
						this.totalResultado = resultado.metadata.total;
						this.pagina = resultado.metadata.page;
						this.tratarDados(resultado.items);
					},
					err => {
						this.totalResultado = 0;
						this.resultado = [];
						this.criarMensagem('error', err.error.message);
					}
				)
			);
		}
	}

	private criarColunas() {
		this.colunas = [
			{ field: 'id', header: 'id' },
			{ field: 'nome', header: 'Incentivo' },
			{ field: 'ativo', header: 'Ativo' }
		];
	}

	private tratarDados(itens: any[]) {
		for (const item of itens) {
			const resultadoTratado = {
				id: item.id,
				nome: item.nome,
				ativo: item.ativo ? 'Sim' : 'Não'
			};
			this.resultado.push(resultadoTratado);
		}
	}

	private salvarNovo(incentivo: TabelasAdministrativas) {
		this.subscriptions.push(
			this.tabelaService.incluirIncentivo(incentivo).subscribe(
				(res: any) => {
					this.exibirMensagemSucesso(res);
				},
				err => {
					this.criarMensagem('error', err.error.message);
				}
			)
		);
	}

	private alterar(id: number, incentivo: TabelasAdministrativas) {
		this.subscriptions.push(
			this.tabelaService.alterarIncentivo(id, incentivo).subscribe(
				(res: any) => {
					this.exibirMensagemSucesso(res);
				},
				err => {
					this.criarMensagem('error', err.error.message);
				}
			)
		);
	}

	private criarMensagem(criticidade: string, mensagem: string) {
		this.messageService.clear();
		this.messageService.add(this.ui.criarMensagem(criticidade, '', mensagem));
	}

	private exibirMensagemSucesso(res: any) {
		if (res) {
			this.criarMensagem('success', res.message);
			this.limparPesquisa();
		}
	}

	private limparPesquisa() {
		this.limparFormularioPesquisa = true;
		this.primeiroItemResultado = 0;
		this.pagina = 1;
		this.pesquisar();
	}
}
